// Dashboard de Compras (Financiero → Resumen). Solo documentos recibidos que
// pasaron por "Procesar Recibidos" (financiero.tickets_buy): facturas y notas de
// crédito, con retenciones aplicadas y desglose por categoría (proceso).
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, Cell, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { FileMinus, FileText, Layers, Loader2, Receipt, ShoppingCart, Users, Wallet } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { apiClient } from '@/lib/api/client';
import { GLASS, GLASS_BG } from '@/lib/glass';
import { LOCALE } from '@/lib/utils/regional';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };
const usd = (v: number | string | null | undefined) =>
  Number(v || 0).toLocaleString(LOCALE, { style: 'currency', currency: 'USD' });
const num = (v: number | string | null | undefined) => Number(v || 0).toLocaleString(LOCALE);
const pct = (part: number | string | null | undefined, total: number | string | null | undefined) =>
  `${(Number(total) ? (Number(part || 0) / Number(total)) * 100 : 0).toFixed(1)}%`;
const MES_CORTO = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const COLORES = ['#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#64748b'];

interface Resumen {
  facturas_docs: number; facturas_monto: number; facturas_subtotal: number; facturas_iva: number;
  nc_docs: number; nc_monto: number; nc_aplicadas: number; nc_monto_aplicado: number;
  ret_docs: number; ret_monto: number; anticipos_monto: number; pagado: number;
  compra_neta: number; por_pagar: number; proveedores: number;
}
interface DashCompras {
  anio: number; resumen: Resumen;
  por_mes: Array<{ mes: number; facturas: number; nc: number; neto: number }>;
  por_dia?: Array<{ dia: number; facturas: number; nc: number; neto: number }>;
  por_categoria: Array<{ categoria: string; docs: number; facturas: number; nc: number; neto: number }>;
  top_proveedores: Array<{ proveedor: string | null; docs: number; neto: number }>;
}

export function DashboardCompras() {
  const anioActual = new Date().getFullYear();
  const [anio, setAnio] = useState(anioActual);
  const [mes, setMes] = useState<number | ''>('');
  const { data, isFetching } = useQuery<DashCompras>({
    queryKey: ['dashCompras', anio, mes],
    queryFn: async () => (await apiClient.post('/rpc/fin_dashboard_compras', { _anio: anio, _mes: mes || null }, FIN)).data,
  });
  const r = data?.resumen;
  const chartMes = mes
    ? (data?.por_dia ?? []).map((d) => ({ mes: String(d.dia), Facturas: Number(d.facturas), 'Notas de crédito': Number(d.nc), Neto: Number(d.neto) }))
    : (data?.por_mes ?? []).map((m) => ({ mes: MES_CORTO[m.mes - 1], Facturas: Number(m.facturas), 'Notas de crédito': Number(m.nc), Neto: Number(m.neto) }));
  const chartCat = (data?.por_categoria ?? []).map((c) => ({ categoria: c.categoria, neto: Number(c.neto) }));

  return (
    <div className={`p-6 space-y-5 min-h-full ${GLASS_BG}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <ShoppingCart className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Dashboard de Compras</h1>
            <p className="text-sm text-muted-foreground">Documentos recibidos procesados (facturas y notas de crédito) · {anio}{mes ? ` · ${MES_CORTO[mes - 1]}` : ''}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={anio} onChange={(e) => setAnio(Number(e.target.value))}>
            {Array.from({ length: anioActual - 2014 }, (_, i) => anioActual - i).map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={mes} onChange={(e) => setMes(e.target.value ? Number(e.target.value) : '')}>
            <option value="">Todo el año</option>
            {MES_CORTO.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
        </div>
      </div>

      {isFetching && !data ? (
        <div className="py-16 text-center"><Loader2 className="w-7 h-7 animate-spin mx-auto text-primary" /></div>
      ) : !r ? (
        <p className="py-16 text-center text-muted-foreground">Sin datos.</p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi icono={FileText} color="text-blue-600" titulo="Facturas" valor={usd(r.facturas_monto)} sub={`${num(r.facturas_docs)} documentos`} />
            <Kpi icono={FileMinus} color="text-red-600" titulo="Notas de crédito" valor={usd(r.nc_monto)} sub={`${num(r.nc_docs)} docs · ${num(r.nc_aplicadas)} aplicadas (${usd(r.nc_monto_aplicado)})`} />
            <Kpi icono={Receipt} color="text-amber-600" titulo="Retenciones aplicadas" valor={usd(r.ret_monto)} sub={`${num(r.ret_docs)} aplicaciones`} />
            <Kpi icono={ShoppingCart} color="text-indigo-700" titulo="Compra neta" valor={usd(r.compra_neta)} sub="Facturas − NC" />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <Kpi small titulo="Subtotal" valor={usd(r.facturas_subtotal)} />
            <Kpi small titulo="IVA" valor={usd(r.facturas_iva)} />
            <Kpi small icono={Wallet} color="text-green-600" titulo="Pagado" valor={usd(r.pagado)} />
            <Kpi small titulo="Por pagar" valor={usd(r.por_pagar)} />
            <Kpi small icono={Users} titulo="Proveedores" valor={num(r.proveedores)} />
          </div>

          <Card className={GLASS}>
            <CardHeader className="pb-2"><CardTitle className="text-base">{mes ? `Compras por día · ${MES_CORTO[mes - 1]}` : 'Compras por mes'}</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartMes} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="mes" fontSize={12} />
                  <YAxis fontSize={12} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v: number) => usd(v)} />
                  <Legend />
                  <Bar dataKey="Facturas" fill="#6366f1" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Notas de crédito" fill="#dc2626" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Neto" fill="#10b981" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Categorizado por proceso */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className={GLASS}>
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-1.5"><Layers className="w-4 h-4" /> Compra neta por categoría (proceso)</CardTitle></CardHeader>
              <CardContent>
                {chartCat.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">Sin datos</p> : (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={chartCat} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis type="number" fontSize={11} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                      <YAxis type="category" dataKey="categoria" width={130} fontSize={11} />
                      <Tooltip formatter={(v: number) => usd(v)} />
                      <Bar dataKey="neto" radius={[0, 3, 3, 0]}>
                        {chartCat.map((_, i) => <Cell key={i} fill={COLORES[i % COLORES.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <Card className={GLASS}>
              <CardHeader className="pb-2"><CardTitle className="text-base">Detalle por categoría</CardTitle></CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-3">Categoría</th><th className="py-2 pr-3 text-right">Facturas</th>
                    <th className="py-2 pr-3 text-right">Monto</th><th className="py-2 pr-3 text-right">NC</th><th className="py-2 pr-3 text-right">Neto</th><th className="py-2 text-right">% Part.</th>
                  </tr></thead>
                  <tbody>
                    {(data?.por_categoria ?? []).map((c, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-2 pr-3 capitalize">{c.categoria}</td>
                        <td className="py-2 pr-3 text-right">{num(c.docs)}</td>
                        <td className="py-2 pr-3 text-right whitespace-nowrap">{usd(c.facturas)}</td>
                        <td className="py-2 pr-3 text-right whitespace-nowrap text-red-600">{c.nc ? `-${usd(c.nc)}` : '—'}</td>
                        <td className="py-2 pr-3 text-right whitespace-nowrap font-medium">{usd(c.neto)}</td>
                        <td className="py-2 text-right text-muted-foreground">{pct(c.neto, r.compra_neta)}</td>
                      </tr>
                    ))}
                    {(data?.por_categoria ?? []).length === 0 && (
                      <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">Sin datos</td></tr>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>

          <Card className={GLASS}>
            <CardHeader className="pb-2"><CardTitle className="text-base">Top 10 proveedores por compra neta</CardTitle></CardHeader>
            <CardContent className="overflow-x-auto">
              {(data?.top_proveedores ?? []).length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">Sin compras</p>
              ) : (
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-3">Proveedor</th><th className="py-2 pr-3 text-right">Facturas</th><th className="py-2 pr-3 text-right">Compra neta</th><th className="py-2 text-right">% Part.</th>
                  </tr></thead>
                  <tbody>
                    {(data?.top_proveedores ?? []).map((c, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-2 pr-3"><p className="truncate max-w-md" title={c.proveedor ?? ''}>{c.proveedor || '—'}</p></td>
                        <td className="py-2 pr-3 text-right">{num(c.docs)}</td>
                        <td className="py-2 pr-3 text-right whitespace-nowrap font-medium">{usd(c.neto)}</td>
                        <td className="py-2 text-right text-muted-foreground">{pct(c.neto, r.compra_neta)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function Kpi({ icono: Icono, color = 'text-muted-foreground', titulo, valor, sub, small }: {
  icono?: React.ComponentType<{ className?: string }>; color?: string; titulo: string; valor: string; sub?: string; small?: boolean;
}) {
  return (
    <Card className={GLASS}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
          {Icono && <Icono className={`w-4 h-4 ${color}`} />}{titulo}
        </div>
        <p className={`font-bold ${small ? 'text-lg' : 'text-2xl'} mt-1`}>{valor}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </CardContent>
    </Card>
  );
}
